import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  Information,
  ProductTDM,
  TdmFindOrder,
  TdmOrder,
} from 'src/app/models/tdm/tdm';
import {
  TdmCardFilter,
  TdmCard,
} from 'src/app/models/tdm/cards';
@Injectable({
  providedIn: 'root',
})
export class TdmService {
  readonly API = environment.config.endpoints.SFM;
  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  information: Information = null;
  private readonly informationDataSource = new BehaviorSubject<Information>(
    this.information,
  );
  currentInformationData$ = this.informationDataSource.asObservable();

  tdmOrder: TdmOrder = null;
  private readonly tdmOrderDataSource = new BehaviorSubject<TdmOrder>(this.tdmOrder);
  currentTdmOrderData$ = this.tdmOrderDataSource.asObservable();

  constructor(private readonly http: HttpClient) {}

  updateCurrentInformationData(information: Information) {
    this.informationDataSource.next(information);
  }

  updateCurrentTdmOrderData(tdmOrder: TdmOrder) {
    this.tdmOrderDataSource.next(tdmOrder);
  }

  getDocumentTypes(): Observable<any> {
    const ENDPOINT = `${this.API}/commonList/findAllDocumentType`;
    return this.http.get<Array<any>>(ENDPOINT, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  getTestTypes(): Observable<any> {
    const ENDPOINT = `${this.API}/commonList/findAllTestType`;
    return this.http.get<Array<any>>(ENDPOINT, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  findOrdersByFilter(tdmFindOrder: TdmFindOrder): Observable<any> {
    const ENDPOINT = `${this.API}/orders/findOrdersByFilters`;
    return this.http.post<any>(ENDPOINT, tdmFindOrder, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  createOrder(tdmOrder: TdmOrder): Observable<any> {
    const ENDPOINT = `${this.API}/orders/create`;
    return this.http.post<any>(ENDPOINT, tdmOrder, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  updateOrder(tdmOrder: TdmOrder): Observable<any> {
    const ENDPOINT = `${this.API}/orders/update`;
    return this.http
      .post<any>(ENDPOINT, tdmOrder, this.httpOptions)
      .pipe(map((response) => response));
  }

  createView(tdmView: any): Observable<any> {
    const ENDPOINT = `${this.API}/views/create`;
    return this.http.post<any>(ENDPOINT, tdmView, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  updateView(tdmView: any): Observable<any> {
    const ENDPOINT = `${this.API}/views/update`;
    return this.http.post<any>(ENDPOINT, tdmView, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  findViewsByFilter(tdmView: any): Observable<any> {
    const ENDPOINT = `${this.API}/views/findViewsByFilters`;
    return this.http.post<any>(ENDPOINT, tdmView, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  createProduct(tdmProduct: any): Observable<any> {
    const ENDPOINT = `${this.API}/products/create`;
    return this.http.post<any>(ENDPOINT, tdmProduct, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  findAllViews(): Observable<any> {
    const ENDPOINT = `${this.API}/views/findAll`;
    return this.http.get<Array<any>>(ENDPOINT, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  updateProduct(tdmProduct: any): Observable<any> {
    const ENDPOINT = `${this.API}/products/update`;
    return this.http.post<any>(ENDPOINT, tdmProduct, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  findProductByName(nameOfProduct: string): Observable<any> {
    const ENDPOINT = `${this.API}/products/findByName/${nameOfProduct}`;
    return this.http.get<Array<any>>(ENDPOINT, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  findAllProducts(): Observable<any> {
    const ENDPOINT = `${this.API}/products/findAll`;
    return this.http.get<Array<any>>(ENDPOINT, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  findAllValidProducts(): Observable<Array<ProductTDM>> {
    const ENDPOINT = `${this.API}/products/findAllValidProducts`;
    return this.http.get<any>(ENDPOINT, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  findProductById(idProduct: number): Observable<any> {
    const ENDPOINT = `${this.API}/products/findById/${idProduct}`;
    return this.http.get<any>(ENDPOINT, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  createCards(tdmCard: TdmCard): Observable<any> {
    const ENDPOINT = `${this.API}/card/create`;
    return this.http.post<any>(ENDPOINT, tdmCard, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  updateCard(tdmCard: TdmCard): Observable<any> {
    const ENDPOINT = `${this.API}/card/update`;
    return this.http
      .post<any>(ENDPOINT, tdmCard, this.httpOptions)
      .pipe(map((response) => response));
  }

  findByFilters(tdmCardFilter: TdmCardFilter): Observable<any> {
    const ENDPOINT = `${this.API}/card/findByFilters`;
    return this.http.post<any>(ENDPOINT, tdmCardFilter, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }

  unlinkCard(numberCard: string): Observable<any> {
    const ENDPOINT = `${this.API}/card/unlinkCard`
    const params = new HttpParams().set('numberCard', numberCard);
    return this.http.post<any>(ENDPOINT, null, { ...this.httpOptions, params })
    .pipe(map((res: any) => res.data), map((response) => response),
    );
  }

  updateCardCounters(orderId: string): Observable<any> {
    const ENDPOINT = `${this.API}/orders/updateCounters/${orderId}`;
    return this.http.post<Array<any>>(ENDPOINT, null, this.httpOptions).pipe(
      map((res: any) => res.data),
      map((response) => response),
    );
  }
}
